/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Quote customer-messaging helpers — pure string builders shared between the
 * web offline path (`src/services/quoteService.ts`) and the notification
 * Cloud Functions (`functions/src/notifications/*`).
 *
 * Before this module, `buildWaUrl` / `buildWhatsappUrl`, the customer
 * message body, the public-quote base URL, and the 8-char ref-code format
 * were each duplicated verbatim (or near-verbatim) across the web service
 * and one or both Cloud Functions. The audit caught the message body had
 * already started to drift in signature.
 *
 * **Structural / import rules**: this module has no imports — no Firebase,
 * no `src/types`, so both halves can import it. The web Vitest suite is the
 * test coverage for both sides.
 */

/**
 * Company display keys — MUST mirror the `IssuingCompany` union in
 * `src/types/common.ts` (no import — the shared/ purity rule; the guard in
 * `__tests__/quoteMessaging.test.ts` pins both directions at compile time).
 */
export const KNOWN_COMPANY_KEYS = [
  'WSS Windows & Doors',
  'Smart Renovation',
  'Pinki Beans',
] as const;
type CompanyKey = (typeof KNOWN_COMPANY_KEYS)[number];
const WSS_COMPANY: CompanyKey = 'WSS Windows & Doors';

/**
 * Customer-facing origins per Firebase project per issuing company. Each
 * entity's Airwallex account only accepts checkouts launched from its own
 * registered domain, so links that reach the CUSTOMER must be minted on the
 * issuing company's origin. Staging is deliberately single-domain (shared
 * sandbox gateway). Unknown projects (e.g. the demo emulator project) fall
 * back to `<id>.web.app`. Exported for the drift test only.
 */
export const COMPANY_PUBLIC_ORIGINS: Record<string, Record<CompanyKey, string>> = {
  'wss-pricing-and-quotation': {
    'WSS Windows & Doors': 'https://app.wss.com.sg',
    'Smart Renovation': 'https://app.smartreno.com.sg',
    'Pinki Beans': 'https://app.pinkibeans.com',
  },
  'wss-erp-staging-f97ec': {
    'WSS Windows & Doors': 'https://app.staging.wss.com.sg',
    'Smart Renovation': 'https://app.staging.wss.com.sg',
    'Pinki Beans': 'https://app.staging.wss.com.sg',
  },
};

/** The environment row (prod/staging) whose origins include `origin`, else null. */
const environmentRowForOrigin = (origin: string): Record<CompanyKey, string> | null => {
  for (const row of Object.values(COMPANY_PUBLIC_ORIGINS)) {
    if (Object.values(row).includes(origin)) return row;
  }
  return null;
};

/**
 * Origins that are LIVE — provisioned end-to-end (Hosting custom domain +
 * DNS + cert + reCAPTCHA allowlist). A company whose mapped origin is not
 * yet in this set resolves to the environment's WSS origin instead, so this
 * code is safe to deploy BEFORE the entity domains exist: links keep
 * working and the wrong-origin heal never targets an unprovisioned host.
 * Ops flip: after the deployment.md runbook's steps 1+3 land for a domain,
 * add its origin here and deploy — per-domain, independently (the same
 * ops-gated pattern as VERIFIED_SENDER_DOMAINS in emailCommon.ts).
 * All four origins live since 2026-08-24 (both entity domains provisioned:
 * Hosting custom domain + DNS + cert + reCAPTCHA allowlist).
 */
export const LIVE_PUBLIC_ORIGINS: ReadonlySet<string> = new Set([
  'https://app.wss.com.sg',
  'https://app.staging.wss.com.sg',
  'https://app.pinkibeans.com',
  'https://app.smartreno.com.sg',
]);

const companyOriginFromRow = (
  row: Record<CompanyKey, string>,
  issuedCompany: string | undefined,
  liveOrigins: ReadonlySet<string>,
): string => {
  const key = (KNOWN_COMPANY_KEYS as readonly string[]).includes(issuedCompany ?? '')
    ? (issuedCompany as CompanyKey)
    : WSS_COMPANY;
  const origin = row[key];
  return liveOrigins.has(origin) ? origin : row[WSS_COMPANY];
};

/**
 * The customer-facing origin for an issuing company. Browser: resolves the
 * environment by membership of `location.origin` in the map; unknown origins
 * (localhost, emulator, preview channels) pass through unchanged so dev and
 * preview flows never mint cross-origin links. Cloud Functions: resolves via
 * `GCLOUD_PROJECT`. Unknown/absent company falls back to the WSS origin
 * (safe for legacy docs). `liveOrigins` gates which mapped origin is actually
 * used — see `LIVE_PUBLIC_ORIGINS`; a company whose origin isn't live yet
 * collapses to the WSS origin regardless of the map entry.
 */
export const getPublicOrigin = (
  issuedCompany?: string,
  liveOrigins: ReadonlySet<string> = LIVE_PUBLIC_ORIGINS,
): string => {
  const g = globalThis as {
    window?: { location: { origin: string } };
    process?: { env?: Record<string, string | undefined> };
  };
  if (typeof g.window !== 'undefined') {
    const row = environmentRowForOrigin(g.window.location.origin);
    if (!row) return g.window.location.origin;
    return companyOriginFromRow(row, issuedCompany, liveOrigins);
  }
  const projectId = g.process?.env?.GCLOUD_PROJECT || 'wss-pricing-and-quotation';
  const row = COMPANY_PUBLIC_ORIGINS[projectId];
  if (!row) return `https://${projectId}.web.app`;
  return companyOriginFromRow(row, issuedCompany, liveOrigins);
};

export const getPublicQuoteBaseUrl = (issuedCompany?: string): string =>
  `${getPublicOrigin(issuedCompany)}/quote`;

/** Build the customer-facing quote URL for a quote id on its company's origin. */
export const buildPublicQuoteUrl = (quoteId: string, issuedCompany?: string): string =>
  `${getPublicQuoteBaseUrl(issuedCompany)}/${quoteId}`;

/** True when `origin` is one of the mapped prod/staging origins. */
export const isKnownPublicOrigin = (origin: string): boolean =>
  environmentRowForOrigin(origin) !== null;

/**
 * The origin a payment-bearing public page SHOULD be on, or null for "do not
 * redirect" (unknown current origin, or already canonical). Backs the §5
 * wrong-origin heal; fires at most once because arriving on the canonical
 * origin yields null.
 */
export const canonicalOriginFor = (
  currentOrigin: string,
  issuedCompany?: string,
  liveOrigins: ReadonlySet<string> = LIVE_PUBLIC_ORIGINS,
): string | null => {
  const row = environmentRowForOrigin(currentOrigin);
  if (!row) return null;
  const canonical = companyOriginFromRow(row, issuedCompany, liveOrigins);
  return canonical === currentOrigin ? null : canonical;
};

/**
 * Derive the short human-facing reference code shown to the customer
 * (e.g. on WhatsApp/email and the printed quote). First 8 chars of the
 * root quote id, uppercased. Callers resolve `rootQuoteId ?? id` before
 * calling — this owns only the *format* decision.
 *
 * Legacy fallback only. New code should use `displayQuoteRef(quote)` so the
 * server-allocated `QTE-2026-0042` number is preferred when present and
 * this UUID-slice format is used only for quotes that pre-date the
 * `quoteNumber` field.
 */
export const buildQuoteRefCode = (rootQuoteIdOrId: string): string =>
  rootQuoteIdOrId.substring(0, 8).toUpperCase();

/**
 * Quote-ref shape sufficient to derive a display string. Kept loose so
 * call sites can pass `QuotePayload`, partial fetched docs, or anything
 * that has the fields below without an explicit cast.
 */
export interface QuoteRefSource {
  id: string;
  quoteNumber?: string;
  metadata?: {
    rootQuoteId?: string;
  };
}

/**
 * Single source of truth for what to display as the quote's reference.
 * Prefers the server-allocated `QTE-2026-0042` number when present; falls
 * back to the legacy 8-char UUID slice for quotes saved before the number
 * field landed. Use everywhere the staff or customer sees a "quote ref".
 */
export const displayQuoteRef = (quote: QuoteRefSource): string =>
  quote.quoteNumber ?? buildQuoteRefCode(quote.metadata?.rootQuoteId ?? quote.id);

/**
 * wa.me deep-link generator. Strips non-digits from the phone number and
 * assumes Singapore (+65) when the local number is a bare 8 digits. A leading
 * `+` means the number already carries its international country code, so it
 * is used as-is even at 8 digits (foreign customers whose WhatsApp is tied to
 * a non-SG number). Returns `null` for empty/invalid input so callers can
 * surface a "mobile missing" message.
 *
 * NOTE: this is deliberately NOT `normalizeMobile` from `utils/phone`.
 * wa.me needs the country code *present*; `normalizeMobile` *strips* it.
 * They are inverse operations — do not merge them.
 */
export const buildWhatsappUrl = (rawMobile: string, message: string): string | null => {
  const hasCountryCode = rawMobile.trim().startsWith('+');
  const digits = rawMobile.replace(/\D/g, '');
  if (!digits) return null;
  const normalised = !hasCountryCode && digits.length === 8 ? `65${digits}` : digits;
  return `https://wa.me/${normalised}?text=${encodeURIComponent(message)}`;
};

export interface CustomerMessageInput {
  customerName?: string | null;
  /** Short ref code — pass the result of `buildQuoteRefCode`. */
  refCode: string;
  /** Full customer-facing quote URL — pass `buildPublicQuoteUrl(quoteId, issuedCompany)`. */
  quoteUrl: string;
  /** Sales rep / company display name. Falls back to "WSS" when empty. */
  authorName?: string | null;
}

/**
 * The pre-filled message body sent on both the WhatsApp and email channels.
 * Single source of truth for the customer-facing copy — edit the wording
 * here and both the offline path and the Cloud Function pick it up (the CF
 * after a functions build re-runs the sync).
 */
export const buildCustomerQuoteMessage = (input: CustomerMessageInput): string => {
  const greeting = input.customerName ? `Hi ${input.customerName}` : 'Hi there';
  return [
    `${greeting},`,
    '',
    `Your quotation (Ref ${input.refCode}) from ${input.authorName || 'WSS'} is ready.`,
    '',
    `Review and accept here: ${input.quoteUrl}`,
    '',
    'To accept, simply pay the deposit shown on the page — that confirms your order.',
  ].join('\n');
};

export interface CustomerOrderMessageInput extends CustomerMessageInput {
  /** Human order number (`ORD-2026-NNNN`) when known; falls back to the ref. */
  orderRef?: string | null;
  /** True once the order is settled in full — drops the pay-balance line. */
  fullySettled?: boolean;
  /** The rep's requested progress payment (spec 2026-10-02): a server-formatted
   *  amount and the validated target. Absent → today's pay-balance line. */
  requested?: { amountText: string; targetPercent: number } | null;
}

/**
 * Post-sale flavour of the customer message — sent once the quote is
 * confirmed/paid (payment links, top-up requests). Speaks of "your order",
 * never "your quotation": the customer has already accepted.
 */
export const buildCustomerOrderMessage = (input: CustomerOrderMessageInput): string => {
  const greeting = input.customerName ? `Hi ${input.customerName}` : 'Hi there';
  const ref = input.orderRef
    ? `${input.orderRef} (Ref ${input.refCode})`
    : `(Ref ${input.refCode})`;
  const payLine = input.fullySettled
    ? `View your order here: ${input.quoteUrl}`
    : input.requested
      ? `Please pay ${input.requested.amountText} to bring your payments to ${input.requested.targetPercent}%: ${input.quoteUrl}`
      : `View your order and pay the balance here: ${input.quoteUrl}`;
  return [
    `${greeting},`,
    '',
    `Your order ${ref} with ${input.authorName || 'WSS'} is confirmed.`,
    '',
    payLine,
  ].join('\n');
};
